import { Component, OnInit, forwardRef, Input } from '@angular/core';
import {
  NG_VALUE_ACCESSOR,
  ControlValueAccessor,
  NG_VALIDATORS,
  Validator,
  ValidationErrors,
  FormControl,
  Validators,
} from '@angular/forms';
import { SelectOption } from '@app/features/create-cv/data-structures/create-cv.interface';
import * as moment from 'moment';
import { BehaviorSubject, tap } from 'rxjs';

@Component({
  selector: 'app-month-year-input',
  templateUrl: './month-year-input.component.html',
  styleUrls: ['./month-year-input.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MonthYearInputComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      multi: true,
      useExisting: forwardRef(() => MonthYearInputComponent),
    },
  ],
})
export class MonthYearInputComponent
  implements OnInit, ControlValueAccessor, Validator
{
  @Input() monthLabel: string;
  @Input() yearLabel: string;
  @Input() isMoreThanCurrentYear = false;
  @Input() set minYear(value: string | undefined) {
    if (value) {
      this._years$.next(this.generateYears(value));
    }
  }

  public disabled: boolean;
  public value = '000000';

  selectedMonthCtrl = new FormControl('');
  selectedYearCtrl = new FormControl('');

  _years$ = new BehaviorSubject<string[]>([]);
  years$ = this._years$
    .asObservable()
    .pipe(tap((years) => (this.years = years)));

  get selectedMonth(): string {
    return this.selectedMonthCtrl.value;
  }

  get selectedYear(): string {
    return this.selectedYearCtrl.value;
  }

  months: SelectOption[] = [];
  years: string[] = [];

  onChanged: any = () => {};
  onTouched: any = () => {};

  constructor() {}

  ngOnInit(): void {
    this.generateMonths();
    this._years$.next(this.generateYears());
  }

  private generateMonths() {
    const monthLabels = [
      'january',
      'february',
      'march',
      'april',
      'may',
      'june',
      'july',
      'august',
      'september',
      'october',
      'november',
      'december',
    ];
    const baseTranslationLabel = 'shared.months.';
    this.months = monthLabels.map((monthLabel, idx) => ({
      code: (idx + 1).toString().padStart(2, '0'),
      value: baseTranslationLabel + monthLabel,
    }));
  }

  private generateYears(minYear?: string): string[] {
    const currentYear = minYear
      ? moment(minYear.substring(0, 4), 'YYYY').get('year')
      : new Date().getFullYear();

    return Array.from({ length: 100 }, (_, i) =>
      minYear || this.isMoreThanCurrentYear ? currentYear + i : currentYear - i
    ).map((year) => year.toString());
  }

  writeValue(val) {
    this.value = val;
    if (typeof val === 'string' && val.length === 6) {
      const selectedYear = this.value.substr(0, 4);
      const selectedMonth = this.value.substr(4, 2);

      this.selectedMonthCtrl.setValue(selectedMonth);
      this.selectedYearCtrl.setValue(selectedYear);
    }
  }

  registerOnChange(fn: any) {
    this.onChanged = fn;
  }
  registerOnTouched(fn: any) {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.selectedMonthCtrl.clearValidators();
      this.selectedYearCtrl.clearValidators();
      this.selectedMonthCtrl.setErrors(null);
      this.selectedYearCtrl.setErrors(null);
      this.selectedMonthCtrl.disable();
      this.selectedYearCtrl.disable();
    } else {
      this.selectedMonthCtrl.setValidators(Validators.required);
      this.selectedYearCtrl.setValidators(Validators.required);
      this.selectedMonthCtrl.enable();
      this.selectedYearCtrl.enable();
    }
    setTimeout(() => {
      this.selectedMonthCtrl.updateValueAndValidity();
      this.selectedYearCtrl.updateValueAndValidity();
    });
  }

  onSelectedItemChange() {
    if (!this.disabled) {
      this.value = this.selectedYear + this.selectedMonth;
      if (this.isValid()) {
        this.onChanged(this.value);
        this.onTouched();
      }
    }
  }

  validate(): ValidationErrors | null {
    return this.selectedMonthCtrl.invalid || this.selectedYearCtrl.invalid
      ? { invalid: true }
      : null;
  }

  private isValid(): boolean {
    return (
      this.value.substr(0, 4) !== '0000' &&
      this.value.substr(0, 4).length === 4 &&
      this.years.includes(this.value.substr(0, 4)) &&
      this.value.substr(4, 2) !== '00' &&
      this.value.substr(4, 2).length === 2 &&
      !!this.months.find(
        (month: SelectOption) => month.code === this.value.substr(4, 2)
      )
    );
  }
}
